import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfoBox from "common-ui-components/components/info-box/InfoBox";
import Table from "common-ui-components/components/table/Table";
import StatusPill from "common-ui-components/components/pill-cutout/StatusPill";
import API from "common-ui-components/components/utils/API";
import InfoBoxRedirectButton from "app/common/InfoBoxRedirectButton";
import styles from "./Patch.module.css";

const REQUEST_PAYLOAD = {
  columns: [],
  sort: {},
  search: [],
  filter: [
    {
      column: "executionStatus",
      filterValue: ["Failed"],
    },
  ],
};

const executionStatusConfig = {
  success: { textColor: "#1e8e4e", bgColor: "#e6f7ed" },
  installed: { textColor: "#1e8e4e", bgColor: "#e6f7ed" },
  "already installed": { textColor: "#1e8e4e", bgColor: "#e6f7ed" },
  failed: { textColor: "#d93025", bgColor: "#fde8e4" },
  "in progress": { textColor: "#c2185b", bgColor: "#fde8f0" },
  pending: { textColor: "#92650a", bgColor: "#fdedc9" },
  default: { textColor: "#6b7280", bgColor: "#eef0f2" },
};

const severityStatusConfig = {
  critical: { textColor: "#d93025", bgColor: "#fde8e4" },
  high: { textColor: "#b98900", bgColor: "#fdf0c6" },
  medium: { textColor: "#3b6ee0", bgColor: "#dce7fd" },
  moderate: { textColor: "#3b6ee0", bgColor: "#dce7fd" },
  low: { textColor: "#6b7280", bgColor: "#e7e9ec" },
  unknown: { textColor: "#6b7280", bgColor: "#eef0f2" },
  default: { textColor: "#6b7280", bgColor: "#eef0f2" },
};

const normalizeExecutionStatus = (value) => {
  if (value === null || value === undefined || value === "") return "--";

  const normalized = String(value).trim().toLowerCase();
  if (["already installed", "installed", "success"].includes(normalized)) {
    return "Success";
  }

  return String(value).trim();
};

const PatchExecutionStatusPill = ({ record }) => {
  const status = normalizeExecutionStatus(record?.patchDeviceStatus);
  const key = status.toLowerCase();
  const config = executionStatusConfig[key] || executionStatusConfig.default;

  return <StatusPill statusConfig={config}>{status}</StatusPill>;
};

const normalizeSeverity = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  return String(value).trim();
};

const PatchSeverityPill = ({ record }) => {
  const severity = normalizeSeverity(record?.severity).toLowerCase();
  const config = severityStatusConfig[severity] || severityStatusConfig.default;

  return (
    <StatusPill statusConfig={config}>
      {normalizeSeverity(record?.severity)}
    </StatusPill>
  );
};

const columns = [
  { title: "Device", accessor: "deviceName" },
  { title: "Patch", accessor: "title" },
  {
    title: "Status",
    accessor: "patchDeviceStatus",
    WrapperComponent: ({ record }) => (
      <PatchExecutionStatusPill record={record} />
    ),
  },
  {
    title: "Severity",
    accessor: "severity",
    WrapperComponent: ({ record }) => <PatchSeverityPill record={record} />,
  },
  { title: "Remarks", accessor: "remarks" },
];

const FailedPatchDeployments = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFailedDeployments = useCallback(async (signal) => {
    try {
      const response = await API.post(
        "/patch/deployment-history",
        REQUEST_PAYLOAD,
        { signal },
      );
      if (signal.aborted) return;

      if (response?.status?.success) {
        const result = response.body?.result;
        setRecords(Array.isArray(result) ? result.slice(0, 5) : []);
      } else {
        setRecords([]);
      }
    } catch (error) {
      if (!signal.aborted) {
        console.error("Failed to load failed patch deployments:", error);
        setRecords([]);
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchFailedDeployments(controller.signal);
    return () => controller.abort();
  }, [fetchFailedDeployments]);

  return (
    <InfoBox
      header="Failed Patch Deployments"
      subtitle="Deployment failures during the last 30 days"
      badge={
        <InfoBoxRedirectButton
          onClick={() => navigate("/app/secure/deployment/apd")}
          label="View all patch deployments"
        />
      }
      badgeProps={{ className: styles.redirectBadge }}
      style={{ minWidth: 0 }}
      childrenProps={{ className: styles.tableContent }}
    >
      <Table
        columns={columns}
        records={records}
        total={loading ? null : records.length}
        skeletonRowCount={5}
      />
    </InfoBox>
  );
};

export default FailedPatchDeployments;
